export default class Waypoint {
	/**
	 * @param {Object} params
	 * @param {Number} params.order - Determine the order in which the waypoints will be visited
	 * @param {Number} params.cx - Longitude value in degrees, ranging from -180 to 180
	 * @param {Number} params.cy - Latitude value in degrees, ranging from -90 to 90
	 * @param {String} params.job_type - Type of service, must be either PICKUP or DELIVERY
	 * @param {Number} params.priority - Determines how important it is to visit this point, where 0 is not important at all and 100
	 * @param {String} params.service_time - Determines the waiting time at the point in the format HH:MM:SS, allowing more than 24 hours, e.g., 34:00:00
	 * @param {Array} params.capacity - List of capacity objects
	 * @returns {Waypoint}
	 * @constructor
	 * @public
	 * @example
	 */
	constructor({
		order,
		cx,
		cy,
		job_type,
		priority,
		service_time,
		capacity,
		ETA,
		ETD,
		added_on,
		address,
		city,
		company_id,
		company_name,
		country,
		department,
		distance_to_next_wp,
		duration_to_next_wp,
		event_date_prog,
		event_id,
		event_status_id,
		event_status_name,
		event_visit_point_user_id,
		id,
		polyline_to_next_wp,
		update_on,
	}) {
		this.order = order ? order : 1;
		this.cx = cx ? cx : null;
		this.cy = cy ? cy : null;
		this.job_type = job_type ? job_type : "PICKUP";
		this.priority = priority ? priority : 100;
		this.service_time = service_time ? service_time : "00:10:00";
		this.capacity = capacity;

		// this fields are not used to create a waypoint, but are used to display information about the waypoint
		this.ETA = ETA ? ETA : "00:00:00";
		this.ETD = ETD ? ETD : "00:00:00";
		this.added_on = added_on ? added_on : "";
		this.address = address ? address : "";
		this.city = city ? city : "";
		this.company_id = company_id ? company_id : 0;
		this.company_name = company_name ? company_name : "";
		this.country = country ? country : "";
		this.department = department ? department : "";
		this.distance_to_next_wp = distance_to_next_wp ? distance_to_next_wp : 0;
		this.duration_to_next_wp = duration_to_next_wp ? duration_to_next_wp : "00:00:00";
		this.event_date_prog = event_date_prog ? event_date_prog : "";
		this.event_id = event_id ? event_id : 0;
		this.event_status_id = event_status_id ? event_status_id : 0;
		this.event_status_name = event_status_name ? event_status_name : "ASSIGNED";
		this.event_visit_point_user_id = event_visit_point_user_id ? event_visit_point_user_id : null;
		this.id = id ? id : 0;
		this.polyline_to_next_wp = polyline_to_next_wp ? polyline_to_next_wp : "";
		this.update_on = update_on ? update_on : null;
	}

	/**
	 * Gets the latitude of the waypoint.
	 *
	 * @returns {number} The latitude value.
	 */
	getLatitude() {
		return this.cy;
	}

	setLatitude(latitude) {
		this.cy = latitude;
		return this;
	}

	/**
	 * Gets the longitude of the waypoint.
	 *
	 * @returns {number} The longitude value.
	 */
	getLongitude() {
		return this.cx;
	}

	/**
	 * Sets the latitude for the waypoint.
	 *
	 * @param {number} latitude - The latitude value to set.
	 * @returns {Waypoint} The current instance of the Waypoint for method chaining.
	 */
	setLongitude(longitude) {
		this.cx = longitude;
		return this;
	}

	/**
	 * Gets the priority of the waypoint.
	 *
	 * @returns {number} The priority of the waypoint.
	 */
	getPriority() {
		return this.priority;
	}

	/**
	 * TODO: This method should be refactored to receive a value between 0 and 100 and multiply it by 10 to set the priority
	 *
	 * @param {Number} priority - Determines how important it is to visit this point, where 0 is not important at all and 100
	 * @returns {void}
	 */
	setPriority(priority) {
		this.priority = priority * 10;
		return this;
	}

	/**
	 * Gets the service time of the waypoint.
	 *
	 * @returns {string} The service time of the waypoint.
	 */
	getServiceTime() {
		return this.service_time;
	}

	/**
	 * Sets the service time of the waypoint.
	 *
	 * @param {String} service_time - Determines the waiting time at the point in the format HH:MM:SS, allowing more than 24 hours, e.g., 34:00:00
	 * @returns {void}
	 */
	setServiceTime(service_time) {
		this.service_time = service_time;
		return this;
	}

	getJobType() {
		return this.job_type;
	}

	setJobType(job_type) {
		this.job_type = job_type;
		return this;
	}

	getOrder() {
		return this.order;
	}

	setOrder(order) {
		this.order = order;
		return this;
	}

	isValid() {
		if (this.cx === null || this.cy === null) {
			return false;
		}
		return true;
	}

	/**
	 * Retrieves the data necessary to create a waypoint.
	 *
	 * @returns {Object} An object containing the waypoint data.
	 * @returns {number} return.order - The order of the waypoint.
	 * @returns {number} return.cx - The x-coordinate of the waypoint.
	 * @returns {number} return.cy - The y-coordinate of the waypoint.
	 * @returns {string} return.job_type - The type of job associated with the waypoint.
	 * @returns {number} return.priority - The priority level of the waypoint.
	 * @returns {number} return.service_time - The service time required at the waypoint.
	 */
	getDataToCreate() {
		return {
			order: this.order,
			cx: this.cx,
			cy: this.cy,
			job_type: this.job_type,
			priority: this.priority,
			service_time: this.service_time,
		};
	}

	/**
	 * Retrieves the data required to update the waypoint.
	 *
	 * @returns {Object} An object containing the updated data for the waypoint.
	 * @returns {number} return.cx - The x-coordinate of the waypoint.
	 * @returns {number} return.cy - The y-coordinate of the waypoint.
	 * @returns {string} return.job_type - The type of job associated with the waypoint.
	 * @returns {number} return.service_time - The service time at the waypoint.
	 */
	getDataToUpdate() {
		return {
			cx: this.cx,
			cy: this.cy,
			job_type: this.job_type,
			service_time: this.service_time,
		};
	}

	/**
	 * Retrieves the data necessary to reorder waypoints.
	 *
	 * @returns {Object} An object containing the order and id of the waypoint.
	 * @returns {number} return.order - The order of the waypoint.
	 * @returns {string} return.id - The ID of the waypoint.
	 */
	getDataToReorder() {
		return {
			order: this.order,
			id: this.id,
		};
	}
}
